import { useEffect, useState } from "react";
import { fetchVehicleSourceById } from "../../../../services/vehicleSourceApi";
import { formatVehicleMarketSourceTypeLabel } from "../../../../constants/vehicleMarketSourceConstants";
import {
  VEHICLE_ONLY_PRODUCTION_TOOLTIP,
  deriveComplianceDisplay,
  deriveRegistryBadge,
  isVehicleOnlyRow,
  publicDisplayDisplayLabel,
  scheduleDisplayLabel,
} from "./vehicleMarketSourceFormat";

const VehicleMarketSourceDetailPanel = ({ row, onClose }) => {
  const [vehicleDetail, setVehicleDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (!row?.vehicleSourceId) {
      setVehicleDetail(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setErrorMessage("");
    fetchVehicleSourceById(row.vehicleSourceId)
      .then((source) => {
        if (!cancelled) setVehicleDetail(source || null);
      })
      .catch((err) => {
        if (!cancelled) setErrorMessage(err.message || "Unable to load Vehicle Market source detail.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [row?.vehicleSourceId]);

  if (!row) return null;

  const compliance = deriveComplianceDisplay(row);
  const registryBadge = deriveRegistryBadge(row);
  const vehicleOnly = isVehicleOnlyRow(row);

  return (
    <div className="admin-re-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="admin-re-modal panel-card admin-re-modal--wide"
        role="dialog"
        aria-labelledby="vehicle-market-detail-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="admin-re-modal-header">
          <div>
            <p className="eyebrow">Vehicle Market source</p>
            <h2 id="vehicle-market-detail-title">{row.displayName}</h2>
            <p className="muted admin-re-source-code">
              {row.vehicleSourceKey || row.automotiveSourceKey || row.id}
            </p>
          </div>
          <button type="button" className="midpoint-share-button" onClick={onClose}>
            Close
          </button>
        </header>

        <div className="admin-re-modal-body">
          {vehicleOnly ? (
            <p className="muted admin-source-controls-dash__type-note">{VEHICLE_ONLY_PRODUCTION_TOOLTIP}</p>
          ) : null}

          <dl className="admin-detail-dl">
            <dt>Registry</dt>
            <dd title={registryBadge.tooltip}>{registryBadge.label}</dd>
            <dt>Type</dt>
            <dd>{formatVehicleMarketSourceTypeLabel(row)}</dd>
            <dt>Data role</dt>
            <dd>{row.dataRole || "—"}</dd>
            <dt>Compliance</dt>
            <dd title={compliance.tooltip}>{compliance.label}</dd>
            <dt>Allowed to ingest</dt>
            <dd>
              {row.allowedToIngest == null ? "—" : row.allowedToIngest ? "Yes" : "No"}
            </dd>
            <dt>Source status</dt>
            <dd>{row.sourceStatus || "—"}</dd>
            <dt>Priority</dt>
            <dd>{row.priority ?? vehicleDetail?.priority ?? "—"}</dd>
            <dt>Health score</dt>
            <dd>{row.healthScore ?? vehicleDetail?.healthScore ?? "—"}</dd>
            <dt>Robots review</dt>
            <dd>{row.robotsReviewStatus || vehicleDetail?.robotsReviewStatus || "—"}</dd>
            <dt>Terms review</dt>
            <dd>{row.termsReviewStatus || vehicleDetail?.termsReviewStatus || "—"}</dd>
            <dt>Access review</dt>
            <dd>{row.accessReviewStatus || vehicleDetail?.accessReviewStatus || "—"}</dd>
            <dt>Recommended action</dt>
            <dd>{row.recommendedActionLabel || row.recommendedAction || "—"}</dd>
            {!vehicleOnly ? (
              <>
                <dt>Manual ingest</dt>
                <dd title={(row.readinessWarnings || []).join(" · ") || undefined}>
                  {row.ingestValidationLabel || row.adapterStatus || "—"}
                </dd>
                <dt>Runnable status</dt>
                <dd>{row.runnableStatus || "—"}</dd>
                <dt>Image support</dt>
                <dd>{row.imageSupportStatus || "—"}</dd>
                <dt>Schedule</dt>
                <dd title={row.scheduleDisabledReason || undefined}>{scheduleDisplayLabel(row)}</dd>
                <dt>Public display</dt>
                <dd title={row.publicDisplayDisabledReason || publicDisplayDisplayLabel(row).tooltip}>
                  {publicDisplayDisplayLabel(row).label}
                </dd>
                <dt>Run blocker</dt>
                <dd>{row.runBlocker && row.runBlocker !== "none" ? row.runBlocker : "—"}</dd>
                <dt>Schedule blocker</dt>
                <dd>
                  {row.scheduleBlocker && row.scheduleBlocker !== "none" ? row.scheduleBlocker : "—"}
                </dd>
                {row.readinessWarnings?.length ? (
                  <>
                    <dt>Ingest warnings</dt>
                    <dd>{row.readinessWarnings.join(" · ")}</dd>
                  </>
                ) : null}
              </>
            ) : null}
          </dl>

          {row.vehicleSourceId ? (
            <>
              {loading ? <p className="muted">Loading compliance record…</p> : null}
              {errorMessage ? <p className="admin-data-health-error">{errorMessage}</p> : null}
              {vehicleDetail ? (
                <dl className="admin-detail-dl">
                  <dt>Base URL</dt>
                  <dd>{vehicleDetail.baseUrl || "—"}</dd>
                  <dt>Notes</dt>
                  <dd>{vehicleDetail.complianceNotes || vehicleDetail.notes || "—"}</dd>
                  <dt>Discovery</dt>
                  <dd>
                    {row.capabilities?.supportsDiscovery
                      ? "Adapter available"
                      : "Not configured for this source"}
                  </dd>
                </dl>
              ) : null}
            </>
          ) : (
            <p className="muted">
              No compliance overlay record. This is an ingest-only or reference Vehicle Market source.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default VehicleMarketSourceDetailPanel;
